import React from 'react';
import styled from 'styled-components';

// --- Styled Components ---

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
//   padding: 2rem 1rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Card = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 650px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.8);
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  padding: 2rem;
  text-align: center;

  h2 {
    margin: 0 0 0.5rem 0;
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.5px;
  }

  p {
    margin: 0;
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const Content = styled.div`
  padding: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  color: #2d3748;
  margin-bottom: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RequirementList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
`;

const RequirementItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #edf2f7;
  color: #4a5568;
  font-size: 0.95rem;

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: #48bb78;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const PricingBox = styled.div`
  background: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 2rem;

  h4 {
    margin: 0 0 0.5rem 0;
    color: #2b6cb0;
    font-size: 1.05rem;
  }

  p {
    margin: 0;
    color: #2c5282;
    font-size: 0.95rem;
    line-height: 1.5;

    strong {
      color: #1a365d;
    }
  }
`;

const PaymentBox = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;

  h4 {
    margin: 0 0 1rem 0;
    color: #2d3748;
    font-size: 1.05rem;
  }
`;

const BankDetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  span {
    display: block;
    font-size: 0.8rem;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.2rem;
  }

  strong {
    font-size: 1rem;
    color: #2d3748;
  }
`;

const EmailNotice = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px dashed #cbd5e0;
  font-size: 0.9rem;
  color: #4a5568;

  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// --- Component ---

export default function ClassRegistrationNotice() {
  return (
    <Container>
      <Card>
        <Header>
          <h2>Before Filling the Form</h2>
          <p>Please review the requirements and payment instructions carefully.</p>
        </Header>
        <Content>
          <SectionTitle>
            <span role="img" aria-label="checklist">📝</span> What You Will Need
          </SectionTitle>
          <RequirementList>
            <RequirementItem>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              Laptop or desktop with webcam and headphones
            </RequirementItem>
            <RequirementItem>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              Gmail Account and Google Meet
            </RequirementItem>
            <RequirementItem>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              Fast Internet Connection (4G or higher recommended)
            </RequirementItem>
            <RequirementItem>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              Distraction-free area for your kids
            </RequirementItem>
          </RequirementList>

          <PricingBox>
            <h4>Investment & Discounts</h4>
            <p>
              Tuition is <strong>N50,000</strong> per child per batch.<br />
              Have more than one child? Get a sibling discount and pay <strong>N45,000</strong> per child.
            </p>
          </PricingBox>

          <PaymentBox>
            <h4>Bank Payment Details</h4>
            <BankDetailGrid>
              <DetailItem>
                <span>Account Name</span>
                <strong>Elexdon Digital Technologies Ltd</strong>
              </DetailItem>
              <DetailItem>
                <span>Account Number</span>
                <strong>1221913704</strong>
              </DetailItem>
              <DetailItem>
                <span>Bank Name</span>
                <strong>Zenith Bank</strong>
              </DetailItem>
            </BankDetailGrid>

            <EmailNotice>
              Please send evidence of payment to{' '}
              <a href="mailto:sales@elexdondigitalacademy.com">
                sales@elexdondigitalacademy.com
              </a>
            </EmailNotice>
          </PaymentBox>
        </Content>
      </Card>
    </Container>
  );
}